import {UI} from "../../../../../../stem-core/src/ui/UIBase";

import {styleRule, StyleSheet} from "../../../../../../stem-core/src/ui/Style";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {countriesDataService} from "../../../../../services/CountriesDataService";
import {Messages} from "../../../../../Messages";
import {NOOP_FUNCTION} from "../../../../../../stem-core/src/base/Utils";
import {SubscriptionFlowType} from "../../../../flows/steps/SubscriptionPlanFlowStep.js";
import {Money} from "../../../../../../stem-core/src/localization/Money.js";
import {isSmallScreen} from "../../../../../UtilsLib";
import {INLINE_EDIT_SUBSCRIPTION_URLS} from "../../../../PanelConstants";
import {Router} from "../../../../../../stem-core/src/ui/Router";
import {BUTTON_TYPE} from "../../../../../Constants";
import {MessageElement} from "../../../../../widget/ui/MessageElement";
import {Flow} from "../../../../flows/Flow.js";
import {iFrameState} from "../../../../../services/IFrameState.js";
import {BlinkButton} from "../../../../../../core/ui/Button.jsx";


class TNRSubscriptionCoverageCardStyle extends StyleSheet {
    @styleRule
    container = {
        minHeight: () => isSmallScreen() ? "" : "484px",
        padding: "18px 24px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        background: this.themeProps.WHITE,
        border: "1px solid #DDDDDD",
        width: () => isSmallScreen() ? null : 418,
    };

    @styleRule
    customHeader = {
        textAlign: "center",
        width: "100%",
        padding: 10,
        top: 0,
        left: 0,
        position: "absolute",
        ...iFrameState.iframeParams.bestValueHeader,
        ...iFrameState.iframeParams.customHeader
    };

    @styleRule
    customHeaderGuard = {
        height: 42,
    };

    @styleRule
    title = {
        fontSize: 24,
        fontWeight: 600,
    };

    @styleRule
    cycleDuration = {
        fontSize: 20,
        marginBottom: 18,
    };

    @styleRule
    pricesBreakdown = {
        width: "100%",
        padding: "12px 6px 0 0",
        borderTop: () => "1px solid #E5E5E5",
        display: "flex",
        justifyContent: "space-between",
        fontSize: 18,
    };

    @styleRule
    afterPricesBreakdown = {
        width: "100%",
        paddingTop: 12,
        borderBottom: () => "1px solid #E5E5E5",
    };

    @styleRule
    pricesBreakdownEntry = {
        padding: 6,
        paddingBottom: 0,
        ":first-child": {
            paddingLeft: 0,
        },
        ":last-child": {
            paddingRight: 0,
        }
    };

    @styleRule
    perkText = {
        fontSize: 16,
        marginTop: 12,
    };

    @styleRule
    perkSubtext = {
        marginLeft: 24,
    };

    @styleRule
    perk = {
        marginTop: 8,
        ":last-child": {
            marginBottom: 18,
        }
    };

    @styleRule
    button = {
        width: "100%",
        bottom: 24,
        ...iFrameState.iframeParams.buttonStyle
    };

    @styleRule
    subscriptionPerksList = {
        marginTop: 18,
        marginBottom: -4,
        fontSize: 16,
    };
}

// TODO: Extend SubscriptionCoverageCard at this point.
@registerStyle(TNRSubscriptionCoverageCardStyle)
export class TNRSubscriptionCoverageCard extends UI.Element {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            offer: null,
            customHeaderType: null,
            onSubscriptionOfferSelected: NOOP_FUNCTION,
            testId: "subscriptionCoverageCard",
        };
    }

    getCustomHeaderColor() {
        const {offer} = this.options;
        const {subscriptionOfferConfigs} = iFrameState.iframeParams;
        const defaultColor = this.options.customHeaderType === "bestValue" ? "#595959" : "#FFD200";
        return subscriptionOfferConfigs?.[offer.id]?.customHeaderColor || defaultColor;
    }

    getCustomHeaderTitle() {
        const {offer} = this.options;
        const {subscriptionOfferConfigs} = iFrameState.iframeParams;
        const defaultTitle = this.options.customHeaderType === "bestValue" ? Messages.bestValue : Messages.adFree;
        return subscriptionOfferConfigs?.[offer.id]?.customHeaderTitle || defaultTitle;
    }

    getCustomHeaderTitleColor() {
        const {offer} = this.options;
        const {subscriptionOfferConfigs} = iFrameState.iframeParams;
        const defaultTitleColor = this.options.customHeaderType === "bestValue" ? "#FFFFFF" : this.themeProps.TEXT_PRIMARY_COLOR;
        return subscriptionOfferConfigs?.[offer.id]?.customHeaderTitleColor || defaultTitleColor;
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        if (this.options.customHeaderType) {
            const borderColor = this.getCustomHeaderColor();
            attr.setStyle("borderColor", borderColor);
            attr.setStyle("boxShadow", `inset 0 0 0 4px ${borderColor}`);
        }
    }

    getOfferLabel() {
        if (Flow.subscriptionPlan.subscriptionFlowType === SubscriptionFlowType.SUBSCRIBE) {
            return Messages.subscribe;
        }
        return Messages.changeSubscription;
    }

    userHasEquivalentSubscription(offer) {
        if (Flow.subscriptionPlan.subscriptionFlowType === SubscriptionFlowType.SUBSCRIBE) {
            return false;
        }
        return Flow.subscriptionPlan.userHasEquivalentSubscriptionToOffer(offer);
    }

    getPricesBreakdown(offer) {
        return [[
            offer.getName(),
            offer.getPrice(),
        ]];
    }

    renderCustomHeaderCardHeader() {
        const {styleSheet} = this;
        const {customHeaderType} = this.options;

        if (!customHeaderType) {
            return null;
        }

        return [
            <div
                className={styleSheet.customHeader}
                style={{
                    "background": this.getCustomHeaderColor(),
                    "color": this.getCustomHeaderTitleColor()
                }}
            >
                {this.getCustomHeaderTitle()}
            </div>,
            <div className={styleSheet.customHeaderGuard}/>
        ];
    }

    renderNameAndDurationSection() {
        const {styleSheet} = this;
        const {offer} = this.options;
        const coverage = offer.getCoverage();

        const {subscriptionOfferConfigs} = iFrameState.iframeParams;

        //Direct mail inline subscription hack
        if (subscriptionOfferConfigs?.[offer.id]?.useCycleDurationAsName) {
            return <div className={styleSheet.title}>
                {offer.billingPlan.getCycleDuration()}
            </div>;
        }

        if (subscriptionOfferConfigs?.[offer.id]?.useOfferAsTitle) {
            return <div className={styleSheet.title}>
                {offer.getName()}
            </div>
        }

        return [
            <div className={styleSheet.title}>
                {coverage.getName()}
            </div>,
            <div className={styleSheet.cycleDuration}>
                {offer.billingPlan.getCycleDuration()}
            </div>,
        ];
    }

    renderPriceBreakdownSection() {
        const {styleSheet} = this;
        const {offer, customHeaderType} = this.options;

        const {subscriptionOfferConfigs} = iFrameState.iframeParams

        const overwrittenPriceBreakdown = subscriptionOfferConfigs?.[offer.id]?.pricesBreakdown;
        if (overwrittenPriceBreakdown) {
            return <div className={styleSheet.pricesBreakdown}>
                <MessageElement message={overwrittenPriceBreakdown} args={[offer.getPrice(), offer.billingPlan.getCycleDuration(true)]}/>
            </div>;
        }

        // Direct mail inline subscription hack
        if (subscriptionOfferConfigs?.[offer.id]?.useOfferNameAsPriceBreakdown) {
            return <div className={styleSheet.pricesBreakdown}>
                <MessageElement message={offer.getName()} />
            </div>;
        }

        return [
            <div className={styleSheet.pricesBreakdown}>
                {this.getPricesBreakdown(offer).map(([offerName, price], index) =>
                    <div className={styleSheet.pricesBreakdownEntry}>
                        <div>{offerName === "Digital" ? "Worldwide" : offerName}</div>
                        <div>{index === 0 && customHeaderType ? <b>{price}</b> : price}</div>
                    </div>)}
            </div>,
        ];
    }

    renderSubscriptionPerksSection() {
        const {styleSheet} = this;
        const {offer} = this.options;
        const coverage = offer.getCoverage();
        const iframeParams = iFrameState.iframeParams;

        // Direct mail inline subscription hack
        const configPerks = iframeParams.subscriptionOfferConfigs ? iframeParams.subscriptionOfferConfigs[offer.id]?.perks : null;
        const items = configPerks ? configPerks.map((perk) => { return {"text": perk}; }) : coverage.getDescription().items || [];

        return [
            <div className={styleSheet.subscriptionPerksList}>{Messages.subscriptionPerksList}</div>,
            <div>
                {items.map(({text, subtext}) =>
                    <div className={styleSheet.perk}>
                        <div className={styleSheet.perkText}>✓ <MessageElement nodeType="span" message={text} /></div>
                        {subtext && <MessageElement nodeType="span" className={styleSheet.perkSubtext} message={subtext} />}
                    </div>
                )}
            </div>
        ];
    }

    renderSubscribeButton() {
        const {styleSheet} = this;
        const {offer, onSubscriptionOfferSelected} = this.options;

        return this.userHasEquivalentSubscription(offer) ?
            <BlinkButton onClick={() => Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancel)} type={BUTTON_TYPE.SECONDARY}>
                {Messages.cancelSubscription}
            </BlinkButton> :
            <BlinkButton className={styleSheet.button} onClick={() => onSubscriptionOfferSelected(offer)}>
                {this.getOfferLabel()}
            </BlinkButton>;
    }

    render() {
        const {styleSheet} = this;

        return [
            this.renderCustomHeaderCardHeader(),
            this.renderNameAndDurationSection(),
            this.renderPriceBreakdownSection(),
            <div className={styleSheet.afterPricesBreakdown} />,
            this.renderSubscriptionPerksSection(),
            <div style={{flex: 1}}/>,
            this.renderSubscribeButton()
        ];
    }

    async onMount() {
        super.onMount();

        // TODO @cleanup we do this to have the country names for the offer.
        await countriesDataService.ensureLoaded();
        this.redraw();
    }
}
